import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

// Scale multiplier for kilo/milli/etc
const SCALE = { '':1, k:1e3, M:1e6, m:1e-3, u:1e-6 };
// Extra power units
const POWER = { W:1, kW:1e3, hp:745.7, 'BTU/h':0.29307107 };

export default function OhmsLawCalc() {
  const [find, setFind] = useState('V'); // V I R P
  const [v, setV] = useState(12);
  const [vU, setVU] = useState('');
  const [i, setI] = useState(2);
  const [iU, setIU] = useState('');
  const [r, setR] = useState(6);
  const [rU, setRU] = useState('');
  const [p, setP] = useState(24);
  const [pU, setPU] = useState('W');

  const scaled = {
    v: Number(v) * SCALE[vU], i: Number(i) * SCALE[iU],
    r: Number(r) * SCALE[rU], p: Number(p) * POWER[pU]
  };

  const result = useMemo(() => {
    let V, I, R, P;
    // Compute the missing quantity using any two knowns
    if (find === 'V') {
      // options: I&R, P&I, P&R
      if (scaled.i && scaled.r) { I = scaled.i; R = scaled.r; V = I*R; P = V*I; }
      else if (scaled.p && scaled.i) { I = scaled.i; P = scaled.p; V = P/I; R = V/I; }
      else if (scaled.p && scaled.r) { R = scaled.r; P = scaled.p; V = Math.sqrt(P*R); I = V/R; }
    } else if (find === 'I') {
      if (scaled.v && scaled.r) { V = scaled.v; R = scaled.r; I = V/R; P = V*I; }
      else if (scaled.p && scaled.v) { V = scaled.v; P = scaled.p; I = P/V; R = V/I; }
      else if (scaled.p && scaled.r) { R = scaled.r; P = scaled.p; I = Math.sqrt(P/R); V = I*R; }
    } else if (find === 'R') {
      if (scaled.v && scaled.i) { V = scaled.v; I = scaled.i; R = V/I; P = V*I; }
      else if (scaled.v && scaled.p) { V = scaled.v; P = scaled.p; R = V*V/P; I = V/R; }
      else if (scaled.i && scaled.p) { I = scaled.i; P = scaled.p; R = P/(I*I); V = I*R; }
    } else { // P
      if (scaled.v && scaled.i) { V = scaled.v; I = scaled.i; P = V*I; R = V/I; }
      else if (scaled.v && scaled.r) { V = scaled.v; R = scaled.r; P = V*V/R; I = V/R; }
      else if (scaled.i && scaled.r) { I = scaled.i; R = scaled.r; P = I*I*R; V = I*R; }
    }
    return { V, I, R, P };
  }, [find, scaled.v, scaled.i, scaled.r, scaled.p]);

  const fmt = (v, dp=4) => v == null || !isFinite(v) ? '—' : Number(v).toFixed(dp);

  const scaleSel = (val, setter) => (
    <select value={val} onChange={e => setter(e.target.value)} className="w-16">
      {Object.keys(SCALE).map(u => <option key={u} value={u}>{u || '—'}</option>)}
    </select>
  );

  return (
    <CalcShell look="cnet" title="Ohm's Law Calculator" subtitle="Solve for V, I, R or P using any two known values (V=IR, P=VI).">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Solve for</label>
            <select value={find} onChange={e => setFind(e.target.value)} data-testid="ohm-find">
              <option value="V">Voltage V</option><option value="I">Current I</option>
              <option value="R">Resistance R</option><option value="P">Power P</option>
            </select>
          </div>
          {find !== 'V' && (
            <div className="field"><label>Voltage V (V)</label>
              <div className="flex gap-2"><input type="number" step="any" value={v} onChange={e => setV(e.target.value)} className="flex-1"/>{scaleSel(vU, setVU)}<span className="self-center">V</span></div>
            </div>
          )}
          {find !== 'I' && (
            <div className="field"><label>Current I (A)</label>
              <div className="flex gap-2"><input type="number" step="any" value={i} onChange={e => setI(e.target.value)} className="flex-1"/>{scaleSel(iU, setIU)}<span className="self-center">A</span></div>
            </div>
          )}
          {find !== 'R' && (
            <div className="field"><label>Resistance R (Ω)</label>
              <div className="flex gap-2"><input type="number" step="any" value={r} onChange={e => setR(e.target.value)} className="flex-1"/>{scaleSel(rU, setRU)}<span className="self-center">Ω</span></div>
            </div>
          )}
          {find !== 'P' && (
            <div className="field"><label>Power P</label>
              <div className="flex gap-2"><input type="number" step="any" value={p} onChange={e => setP(e.target.value)} className="flex-1"/>
                <select value={pU} onChange={e => setPU(e.target.value)}>{Object.keys(POWER).map(u => <option key={u}>{u}</option>)}</select>
              </div>
            </div>
          )}

          {/* Circuit Diagram */}
          <div className="mt-4 p-3 rounded-lg bg-white border border-slate-200">
            <div className="text-center text-xs font-semibold text-slate-600 mb-2">Ohm's Law Circuit Diagram</div>
            <svg viewBox="0 0 320 200" className="w-full max-w-sm mx-auto">
              {/* Wires forming rectangle circuit */}
              <line x1="40" y1="60" x2="280" y2="60" stroke="#0f172a" strokeWidth="2"/>
              <line x1="40" y1="60" x2="40" y2="150" stroke="#0f172a" strokeWidth="2"/>
              <line x1="280" y1="60" x2="280" y2="150" stroke="#0f172a" strokeWidth="2"/>
              <line x1="40" y1="150" x2="130" y2="150" stroke="#0f172a" strokeWidth="2"/>
              <line x1="190" y1="150" x2="280" y2="150" stroke="#0f172a" strokeWidth="2"/>

              {/* Battery symbol (voltage source) — long/short lines */}
              <line x1="20" y1="95" x2="60" y2="95" stroke="#0f172a" strokeWidth="2"/>
              <line x1="28" y1="115" x2="52" y2="115" stroke="#0f172a" strokeWidth="4"/>
              <line x1="40" y1="60" x2="40" y2="95" stroke="#0f172a" strokeWidth="2"/>
              <line x1="40" y1="115" x2="40" y2="150" stroke="#0f172a" strokeWidth="2"/>
              <text x="10" y="90" fontSize="12" fill="#1d4ed8" fontWeight="700">+</text>
              <text x="10" y="128" fontSize="12" fill="#1d4ed8" fontWeight="700">−</text>
              <text x="65" y="108" fontSize="13" fill="#1d4ed8" fontWeight="700">V</text>

              {/* Resistor (zig-zag) */}
              <polyline points="130,150 140,140 150,160 160,140 170,160 180,140 190,150" fill="none" stroke="#0f172a" strokeWidth="2"/>
              <text x="155" y="180" textAnchor="middle" fontSize="13" fill="#dc2626" fontWeight="700">R</text>

              {/* Current arrow */}
              <line x1="90" y1="45" x2="220" y2="45" stroke="#16a34a" strokeWidth="1.5" markerEnd="url(#ohmArr)"/>
              <defs>
                <marker id="ohmArr" markerWidth="10" markerHeight="10" refX="7" refY="5" orient="auto"><polygon points="0 0,10 5,0 10" fill="#16a34a"/></marker>
              </defs>
              <text x="150" y="38" textAnchor="middle" fontSize="13" fill="#16a34a" fontWeight="700">I</text>
            </svg>
            <div className="text-center text-xs text-slate-600 mt-1">V = I × R &nbsp;·&nbsp; P = V × I</div>
          </div>
        </div>
        <div>
          <table className="data-table" data-testid="ohm-result">
            <tbody>
              <tr><td>Voltage V</td><td className="text-right">{fmt(result.V)} V</td></tr>
              <tr><td>Current I</td><td className="text-right">{fmt(result.I)} A</td></tr>
              <tr><td>Resistance R</td><td className="text-right">{fmt(result.R)} Ω</td></tr>
              <tr><td>Power P</td><td className="text-right font-semibold">{fmt(result.P)} W</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <StepsPanel steps={[`V = I × R`, `P = V × I = I² × R = V²/R`, `Any two knowns compute the other two.`]} />
    </CalcShell>
  );
}
